import {ReactNode, useCallback, useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {css, cx} from "@panda/css";

import {Badge} from "@pg-design/badge-module";
import {ClockIcon, FlagPoleIcon} from "@pg-design/icons-module";
import {Modal} from "@pg-design/modal-module";
import {SliderGallery, SliderGalleryIcon} from "@pg-design/slider-gallery-module";
import {Text} from "@pg-design/text-module";
import {AdvancedScroll} from "@pg-mono/advanced-scroll-module";
import {GalleryPicturesGrid, IGalleryImage, usePinchZoom} from "@pg-mono/gallery";
import {compact, uniq} from "@pg-mono/nodash";
import {formatDate} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../../app/rp_reducer";
import {getThumbnailLink} from "../../../app/utils/get_thumbnail_link";
import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {IPropertyDetailOfferMainImage} from "../../../property/types/IPropertyDetailOfferMainImage";
import {IPropertyGallery} from "../../../property/types/IPropertyGallery";
import {getTrackedSiteData} from "../../../tracking/algolytics_hits/tracked_utils/tracked_site";
import {IOfferGallery} from "../../detail/types/IOfferGallery";
import {trackAmplitudeOpenGallery} from "../../tracking/amplitude/track_amplitude_open_gallery";
import {trackAmplitudeViewPhoto} from "../../tracking/amplitude/track_amplitude_view_photo";
import {hitClickOnGalleryClose, hitClickOnGalleryImage, hitClickOnGalleryNext, hitClickOnGalleryPrev, hitGalleryView} from "../../tracking/gallery_events";
import {getConstructionGalleryTagLabel, IConstructionGalleryApiResponse, IConstructionGalleryResponse} from "../../types/ConstructionGallery";
import {IOfferDetail} from "../../types/IOfferDetail";
import {IOfferDetailMainImage} from "../../types/IOfferDetailMainImage";
import {getOffer3dViewLink, getOffer3dViewMonths, hasOffer3dView, resolveOffer3dViewId} from "../../utils/has_offer_3d_view";
import {ConstructionGalleryPaywallTrigger} from "./ConstructionGalleryPaywallTrigger";
import {ConstructionTimelineSection} from "./ConstructionTimelineSection";

const presaleOfferDefaultImg375x212 = require("../../images/presale_offer_default_img_375x212.jpg");
const presaleOfferDefaultImg = require("../../images/presale_offer_default_img.jpg");

interface IProps {
    offer: IPropertyDetailOffer | IOfferDetail;
    propertyGallery?: IPropertyGallery;
    offerGallery?: IOfferGallery;
    constructionGalleryData?: IConstructionGalleryApiResponse | null;
    constructionEndDate?: string | null;
    isConstructionGalleryTab?: boolean;
    showMainImage?: boolean;
    lastGalleryTile?: ReactNode;
    shouldShowVideo?: boolean;
    showAccountPaywall?: boolean;
    propertyId?: number;
    lightboxZIndex?: number;
    /**
     * When provided, the gallery also emits Amplitude `open_gallery` (with this `location`) and
     * `view_photo` events for the lightbox. Used by the tinder multilead deck; absent on the standard
     * offer/property pages, which keep the default `km_offer_details` location and no `view_photo`.
     */
    amplitudeGalleryContext?: {location: string};
}

export interface IGalleryOffer {
    main_image?: IOfferDetailMainImage | IPropertyDetailOfferMainImage | null;
}

export type IGallery = (
    | {
          image: {
              g_img_500: string;
              g_img_750: string;
              g_img_1500: string;
              tags?: {
                  tag_date?: string;
                  tag_status?: string;
              };
          } | null;
      }
    | undefined
)[];

const construtionImagesAltOptions = [
    "zdjęcia z budowy ",
    "plac budowy ",
    "prace budowlane na ",
    "widok placu budowy ",
    "postęp prac na inwestycji ",
    "budowa "
];

const getGalleryImagesAltText = (index: number, offerName: string, isConstruction: boolean) => {
    if (isConstruction) {
        const textIndex = index % construtionImagesAltOptions.length;
        return `${construtionImagesAltOptions[textIndex]}${offerName}`;
    }
    return `zdjęcie ${index + 1} w inwestycji ${offerName}`;
};

export const OfferGalleryModalContent = (props: IProps) => {
    const {
        offer,
        offerGallery,
        propertyGallery,
        constructionGalleryData,
        constructionEndDate,
        isConstructionGalleryTab,
        showMainImage,
        lastGalleryTile,
        shouldShowVideo,
        showAccountPaywall,
        propertyId,
        lightboxZIndex,
        amplitudeGalleryContext
    } = props;
    const constructionGallery = constructionGalleryData?.gallery;
    const apiConstructionTours = constructionGalleryData?.construction_3d_tours;
    const viewType = useSelector((state: IRPStore) => state.viewType);
    const {site, site_id} = getTrackedSiteData();

    const offer3dViewId = resolveOffer3dViewId(offer);
    const groupMainOffer = "group_primary_offer" in offer && offer.group_primary_offer ? offer.group_primary_offer : offer;
    const currentStage = "groups" in groupMainOffer ? groupMainOffer.groups?.stages?.find((stage) => stage.offer.id === offer.id) : undefined;
    const offer3dViewMonths = offer3dViewId != null ? getOffer3dViewMonths(offer3dViewId, currentStage?.id, apiConstructionTours) : [];

    const [selectedDate, setSelectedDate] = useState<string | null>(null);

    // constructionGalleryData resolves asynchronously (RTK Query), so selectedDate starts null and gets its real
    // default only once data is in. Guarded by the ref so a later user pill click isn't overwritten by a refetch.
    const hasInitializedSelectedDateRef = useRef(false);
    useEffect(() => {
        if (hasInitializedSelectedDateRef.current || constructionGalleryData === undefined) {
            return;
        }
        hasInitializedSelectedDateRef.current = true;
        setSelectedDate(getDefaultSelectedDate(constructionGallery, offer3dViewMonths));
    }, [constructionGalleryData, offer3dViewMonths]);

    const filteredConstructionGallery =
        constructionGallery && selectedDate ? constructionGallery.filter((item) => item?.tags?.tag_date?.startsWith(selectedDate)) : constructionGallery;

    const galleryImages = compact(getGalleryImages(offer, {constructionGallery: filteredConstructionGallery, showMainImage, offerGallery, propertyGallery}));

    const [initialGalleryIndex, setInitialGalleryIndex] = useState(0);
    const [galleryCounter, setGalleryCounter] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const {isMobile} = useUserDevice();
    const {isZoomed, containerRef, getImageStyle, reset} = usePinchZoom(isModalOpen);
    const isConstructionGallery = (constructionGallery?.length ?? 0) > 0 && !props.offerGallery && !props.propertyGallery;
    useEffect(() => {
        if (viewType.current) {
            hitGalleryView({
                view_type: viewType.current,
                site,
                site_id,
                offer: offer.id
            });
        }
    }, [viewType.current]);

    useEffect(() => {
        setGalleryCounter(initialGalleryIndex);
    }, [initialGalleryIndex]);

    const trackingSharedData = {
        view_type: viewType.current ? viewType.current : "",
        site,
        site_id,
        offer: offer.id
    };

    const offer3dViewSrc = offer3dViewId != null ? getOffer3dViewLink(offer3dViewId, currentStage?.id, selectedDate, apiConstructionTours) : undefined;
    const hasConstructionTimeline = isConstructionGallery || (!props.offerGallery && !props.propertyGallery && offer3dViewMonths.length > 0);

    const onClose = () => {
        setInitialGalleryIndex(0);
        setIsModalOpen(false);

        hitClickOnGalleryClose(trackingSharedData);
    };

    const onGalleryImageClick = (index: number) => {
        if (showAccountPaywall) {
            return;
        }
        setIsModalOpen(true);
        setInitialGalleryIndex(index);
        hitClickOnGalleryImage({...trackingSharedData, image_position: index});
        const photoSection = isConstructionGalleryTab ? "construction_photos" : "gallery";
        trackAmplitudeOpenGallery({
            location: amplitudeGalleryContext?.location,
            offerId: offer.id,
            propertyId: propertyId,
            galleryView: "viewer",
            photoSection,
            photoIndex: index
        });
        if (amplitudeGalleryContext) {
            trackAmplitudeViewPhoto({
                location: amplitudeGalleryContext.location,
                offerId: offer.id,
                propertyId,
                photoIndex: index,
                photosTotal: galleryImages.length,
                photoSection,
                photoNavigation: "initial"
            });
        }
    };

    const offerGalleryLength = offerGallery?.length || 0;
    const propertyGalleryLength = propertyGallery ? propertyGallery.length : 0;

    const galleryLength = isConstructionGalleryTab ? galleryImages.length - 1 : offerGalleryLength + propertyGalleryLength;

    const handleViewerSlideChange = useCallback(
        (current: number, next: number) => {
            reset();
            setGalleryCounter(next);

            const trackingData = {
                ...trackingSharedData,
                image_position: next
            };

            if (current < next) {
                hitClickOnGalleryNext(trackingData);
            }

            if (current > next) {
                hitClickOnGalleryPrev(trackingData);
            }

            if (amplitudeGalleryContext && current !== next) {
                trackAmplitudeViewPhoto({
                    location: amplitudeGalleryContext.location,
                    offerId: offer.id,
                    propertyId,
                    photoIndex: next,
                    photosTotal: galleryImages.length,
                    photoSection: isConstructionGalleryTab ? "construction_photos" : "gallery",
                    photoNavigation: current < next ? "arrow_next" : "arrow_prev"
                });
            }
        },
        [reset, trackingSharedData, amplitudeGalleryContext, offer.id, propertyId, galleryImages.length, isConstructionGalleryTab]
    );

    const galleryGrid = (
        <GalleryPicturesGrid
            video={offer.video}
            shouldShowVideo={shouldShowVideo}
            getImageAlt={(index, isConstructionImage) => getGalleryImagesAltText(index, offer.name, isConstructionImage ?? isConstructionGallery)}
            images={galleryImages}
            onImageClick={onGalleryImageClick}
            lastTile={lastGalleryTile}
        />
    );

    return (
        <AdvancedScroll
            className={galleryHolder}
            disableScroll={showAccountPaywall}
            scrollTopShadowClassName={displayNone}
            scrollBottomShadowClassName={displayNone}
            contentClassName={scrollStyle}
            height={0}
        >
            {showAccountPaywall && (
                <ConstructionGalleryPaywallTrigger propertyId={propertyId} offerId={offer.id} photosAvailable construction3dTours={apiConstructionTours} />
            )}
            {hasConstructionTimeline && (
                <div className={cx(showAccountPaywall && blurContent)}>
                    <ConstructionTimelineSection
                        constructionGallery={constructionGallery ?? []}
                        constructionDateRange={offer.construction_date_range}
                        constructionEndDate={constructionEndDate}
                        policamLinksMonths={offer3dViewMonths}
                        selectedDate={selectedDate}
                        onDateSelect={setSelectedDate}
                    />
                </div>
            )}
            {isConstructionGalleryTab && hasOffer3dView(offer, apiConstructionTours) && offer3dViewSrc && (
                <section className={gallerySection}>
                    <Text variant="mini_header" className={gallerySectionHeader}>
                        WIDOK 3D
                    </Text>
                    <div className={cx(showAccountPaywall && blurContent)}>
                        {isMobile && (
                            <Text variant="body_copy_2" className={gallerySectionHeader}>
                                użyj palców aby przybliżać i obracać widok
                            </Text>
                        )}
                        <iframe
                            className={gallery3dIframe}
                            src={offer3dViewSrc}
                            title="Widok 3D"
                            sandbox="allow-scripts allow-same-origin allow-pointer-lock allow-fullscreen"
                            referrerPolicy="no-referrer"
                            allow="fullscreen"
                            loading="lazy"
                        />
                    </div>
                </section>
            )}
            <section className={gallerySection}>
                {isConstructionGallery && galleryImages.length > 0 && (
                    <Text variant="mini_header" className={gallerySectionHeader}>
                        ZDJĘCIA
                    </Text>
                )}
                <div className={cx(showAccountPaywall && blurContent)}>{galleryGrid}</div>
            </section>
            {/* Spread instead of zIndex={lightboxZIndex}: an explicit undefined would override the Modal default (9000) */}
            <Modal fullScreen isOpen={isModalOpen} modalTheme="dark" {...(lightboxZIndex === undefined ? {} : {zIndex: lightboxZIndex})} onModalClose={onClose}>
                <Modal.CloseButton placement="right" fillColor="#fff" />
                {isModalOpen && (
                    <Modal.Content>
                        <div ref={containerRef} className={gallerySliderHolder}>
                            <SliderGallery
                                disableSwipe={isZoomed}
                                slides={galleryImages.map((image, index) => (
                                    <div key={index} className={slideContainerStyle}>
                                        {image.tags && (
                                            <div className={tagContainer}>
                                                {image.tags.map((tag, tagIndex) => (
                                                    <Badge variant={tag.variant} icon={tag.icon} iconSize="1.5" key={tagIndex}>
                                                        {tag.label}
                                                    </Badge>
                                                ))}
                                            </div>
                                        )}

                                        {/* Same zoom style on every slide — outgoing slide must keep transform during slider transition. */}
                                        <picture style={getImageStyle()}>
                                            <source media="(min-width: 750px)" srcSet={image?.srcLg} />
                                            <source media="(min-width: 480px)" srcSet={image?.srcMd} />
                                            <img src={image?.srcSm} alt={offer.name} />
                                        </picture>
                                    </div>
                                ))}
                                arrows={{
                                    left: <SliderGalleryIcon position="left" wrapperColor="var(--colors-primary)" />,
                                    right: <SliderGalleryIcon position="right" wrapperColor="var(--colors-primary)" />
                                }}
                                initialSlide={initialGalleryIndex}
                                onSlideChange={handleViewerSlideChange}
                            />

                            <div className={counterHolder}>
                                <Text variant="info_txt_1" color="#fff">
                                    Zdjęcie: {galleryCounter + 1} z {galleryLength + 1}
                                </Text>
                            </div>
                        </div>
                    </Modal.Content>
                )}
            </Modal>
        </AdvancedScroll>
    );
};

/*
    Helper
 */
const getDefaultSelectedDate = (gallery: IConstructionGalleryResponse | undefined, offer3dViewMonths: string[]) => {
    const galleryMonths = (gallery ?? []).filter((item) => item?.tags?.tag_date).map((item) => item!.tags!.tag_date.slice(0, 7));
    const months = uniq([...galleryMonths, ...offer3dViewMonths]);
    return months.length > 0 ? months.sort((prev, next) => next.localeCompare(prev))[0] : null;
};

const getGalleryImagesData = (gallery?: IGallery) => {
    return gallery
        ? gallery
              .map((item) => {
                  const srcSm = item && item.image?.g_img_500;
                  const srcMd = item && item.image?.g_img_750;
                  const srcLg = item && item.image?.g_img_1500;

                  return item ? {srcSm, srcMd, srcLg} : null;
              })
              .filter(Boolean)
        : [];
};

const getConstructionGalleryImagesData = (gallery: IConstructionGalleryResponse) => {
    return gallery
        .map((item) => {
            const srcSm = item && getThumbnailLink(item.image, {width: 500});
            const srcMd = item && getThumbnailLink(item.image, {width: 750});
            const srcLg = item && getThumbnailLink(item.image, {width: 1500});

            const dateTag = item?.tags?.tag_date && {
                label: formatDate(item.tags.tag_date, "MMM yyyy"),
                variant: "label_info",
                icon: ClockIcon
            };
            const statusTag = item?.tags?.tag_status && {
                label: getConstructionGalleryTagLabel(item.tags.tag_status),
                variant: "label_warning",
                icon: FlagPoleIcon
            };

            return item ? {srcSm, srcMd, srcLg, tags: [dateTag, statusTag].filter(Boolean)} : null;
        })
        .filter(Boolean);
};

const getGalleryImages = (
    offer: IGalleryOffer,
    galleries: {showMainImage?: boolean; offerGallery?: IOfferGallery; propertyGallery?: IPropertyGallery; constructionGallery?: IConstructionGalleryResponse}
): (IGalleryImage | null)[] => {
    const propertyGalleryImages = (galleries.propertyGallery && getGalleryImagesData(galleries.propertyGallery)) || [];

    const mainImage = offer.main_image?.m_img_500
        ? {srcSm: offer.main_image?.m_img_500, srcMd: offer.main_image?.m_img_750, srcLg: offer.main_image?.m_img_1500}
        : {srcSm: presaleOfferDefaultImg375x212, srcMd: presaleOfferDefaultImg, srcLg: presaleOfferDefaultImg};

    const offerGalleryImages = getGalleryImagesData(galleries.offerGallery);

    const constructionGalleryImages = (galleries.constructionGallery && getConstructionGalleryImagesData(galleries.constructionGallery)) || [];

    return [...propertyGalleryImages, ...(galleries.showMainImage ? [mainImage] : []), ...offerGalleryImages, ...constructionGalleryImages];
};

const galleryHolder = css({
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",

    md: {
        flexDirection: "row"
    }
});

const gallerySection = css({
    display: "flex",
    flexDirection: "column",
    "&:not(:first-of-type)": {
        marginTop: 4
    }
});

const gallerySectionHeader = css({
    margin: 0,
    color: "white"
});

const gallery3dIframe = css({
    width: "100%",
    height: "56.25vw",
    border: "none",
    aspectRatio: "16/9",

    md: {
        width: "95%"
    }
});

const scrollStyle = css({
    "&::-webkit-scrollbar": {
        width: "4px"
    },
    "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#838595",
        "&:hover": {
            backgroundColor: "#83859599"
        }
    },
    "&::-webkit-scrollbar-track": {
        backgroundColor: "#686975"
    },

    md: {
        "&::-webkit-scrollbar": {
            width: "8px"
        }
    }
});

const displayNone = css({
    display: "none"
});

const blurContent = css({
    filter: "blur(10px)"
});

const gallerySliderHolder = css({
    position: "relative",
    w: "100%",
    h: "100%",
    // touch-action: none — custom pinch/pan + horizontal slider swipe (no native browser zoom).
    touchAction: "none",
    "& picture": {
        width: "100%",
        maxWidth: "100%"
    },
    "& picture img": {
        width: "100%",
        maxWidth: "100%",
        height: "100%",
        objectFit: "contain"
    }
});

const slideContainerStyle = css({
    width: "100%",
    height: "100%",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
});

const counterHolder = css({
    position: "absolute",
    bottom: "5rem",
    left: "50%",
    transform: "translate(-50%, 0%)",
    backgroundColor: "rgba(67, 68, 77, 0.7)",
    px: 2,
    py: 1
});

const tagContainer = css({
    position: "fixed",
    display: "flex",
    color: "var(--colors-secondary)",
    top: 1,
    gap: 1
});
import {ReactNode, useCallback, useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {css, cx} from "@panda/css";

import {Badge} from "@pg-design/badge-module";
import {ClockIcon, FlagPoleIcon} from "@pg-design/icons-module";
import {Modal} from "@pg-design/modal-module";
import {SliderGallery, SliderGalleryIcon} from "@pg-design/slider-gallery-module";
import {Text} from "@pg-design/text-module";
import {AdvancedScroll} from "@pg-mono/advanced-scroll-module";
import {GalleryPicturesGrid, IGalleryImage, usePinchZoom} from "@pg-mono/gallery";
import {compact, uniq} from "@pg-mono/nodash";
import {formatDate} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../../app/rp_reducer";
import {getThumbnailLink} from "../../../app/utils/get_thumbnail_link";
import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {IPropertyDetailOfferMainImage} from "../../../property/types/IPropertyDetailOfferMainImage";
import {IPropertyGallery} from "../../../property/types/IPropertyGallery";
import {getTrackedSiteData} from "../../../tracking/algolytics_hits/tracked_utils/tracked_site";
import {IOfferGallery} from "../../detail/types/IOfferGallery";
import {trackAmplitudeOpenGallery} from "../../tracking/amplitude/track_amplitude_open_gallery";
import {trackAmplitudeViewPhoto} from "../../tracking/amplitude/track_amplitude_view_photo";
import {hitClickOnGalleryClose, hitClickOnGalleryImage, hitClickOnGalleryNext, hitClickOnGalleryPrev, hitGalleryView} from "../../tracking/gallery_events";
import {getConstructionGalleryTagLabel, IConstructionGalleryApiResponse, IConstructionGalleryResponse} from "../../types/ConstructionGallery";
import {IOfferDetail} from "../../types/IOfferDetail";
import {IOfferDetailMainImage} from "../../types/IOfferDetailMainImage";
import {getOffer3dViewLink, getOffer3dViewMonths, hasOffer3dView, resolveOffer3dViewId} from "../../utils/has_offer_3d_view";
import {ConstructionGalleryPaywallTrigger} from "./ConstructionGalleryPaywallTrigger";
import {ConstructionTimelineSection} from "./ConstructionTimelineSection";

const presaleOfferDefaultImg375x212 = require("../../images/presale_offer_default_img_375x212.jpg");
const presaleOfferDefaultImg = require("../../images/presale_offer_default_img.jpg");

interface IProps {
    offer: IPropertyDetailOffer | IOfferDetail;
    propertyGallery?: IPropertyGallery;
    offerGallery?: IOfferGallery;
    constructionGalleryData?: IConstructionGalleryApiResponse | null;
    constructionEndDate?: string | null;
    isConstructionGalleryTab?: boolean;
    showMainImage?: boolean;
    lastGalleryTile?: ReactNode;
    shouldShowVideo?: boolean;
    showAccountPaywall?: boolean;
    propertyId?: number;
    lightboxZIndex?: number;
    /**
     * When provided, the gallery also emits Amplitude `open_gallery` (with this `location`) and
     * `view_photo` events for the lightbox. Used by the tinder multilead deck; absent on the standard
     * offer/property pages, which keep the default `km_offer_details` location and no `view_photo`.
     */
    amplitudeGalleryContext?: {location: string};
}

export interface IGalleryOffer {
    main_image?: IOfferDetailMainImage | IPropertyDetailOfferMainImage | null;
}

export type IGallery = (
    | {
          image: {
              g_img_500: string;
              g_img_750: string;
              g_img_1500: string;
              tags?: {
                  tag_date?: string;
                  tag_status?: string;
              };
          } | null;
      }
    | undefined
)[];

const construtionImagesAltOptions = [
    "zdjęcia z budowy ",
    "plac budowy ",
    "prace budowlane na ",
    "widok placu budowy ",
    "postęp prac na inwestycji ",
    "budowa "
];

const getGalleryImagesAltText = (index: number, offerName: string, isConstruction: boolean) => {
    if (isConstruction) {
        const textIndex = index % construtionImagesAltOptions.length;
        return `${construtionImagesAltOptions[textIndex]}${offerName}`;
    }
    return `zdjęcie ${index + 1} w inwestycji ${offerName}`;
};

export const OfferGalleryModalContent = (props: IProps) => {
    const {
        offer,
        offerGallery,
        propertyGallery,
        constructionGalleryData,
        constructionEndDate,
        isConstructionGalleryTab,
        showMainImage,
        lastGalleryTile,
        shouldShowVideo,
        showAccountPaywall,
        propertyId,
        lightboxZIndex,
        amplitudeGalleryContext
    } = props;
    const constructionGallery = constructionGalleryData?.gallery;
    const apiConstructionTours = constructionGalleryData?.construction_3d_tours;
    const viewType = useSelector((state: IRPStore) => state.viewType);
    const {site, site_id} = getTrackedSiteData();

    const offer3dViewId = resolveOffer3dViewId(offer);
    const groupMainOffer = "group_primary_offer" in offer && offer.group_primary_offer ? offer.group_primary_offer : offer;
    const currentStage = "groups" in groupMainOffer ? groupMainOffer.groups?.stages?.find((stage) => stage.offer.id === offer.id) : undefined;
    const offer3dViewMonths = offer3dViewId != null ? getOffer3dViewMonths(offer3dViewId, currentStage?.id, apiConstructionTours) : [];

    const [selectedDate, setSelectedDate] = useState<string | null>(null);

    // constructionGalleryData resolves asynchronously (RTK Query), so selectedDate starts null and gets its real
    // default only once data is in. Guarded by the ref so a later user pill click isn't overwritten by a refetch.
    const hasInitializedSelectedDateRef = useRef(false);
    useEffect(() => {
        if (hasInitializedSelectedDateRef.current || constructionGalleryData === undefined) {
            return;
        }
        hasInitializedSelectedDateRef.current = true;
        setSelectedDate(getDefaultSelectedDate(constructionGallery, offer3dViewMonths));
    }, [constructionGalleryData, offer3dViewMonths]);

    const filteredConstructionGallery =
        constructionGallery && selectedDate ? constructionGallery.filter((item) => item?.tags?.tag_date?.startsWith(selectedDate)) : constructionGallery;

    const galleryImages = compact(getGalleryImages(offer, {constructionGallery: filteredConstructionGallery, showMainImage, offerGallery, propertyGallery}));

    const [initialGalleryIndex, setInitialGalleryIndex] = useState(0);
    const [galleryCounter, setGalleryCounter] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const {isMobile} = useUserDevice();
    const {isZoomed, containerRef, getImageStyle, reset} = usePinchZoom(isModalOpen);
    const isConstructionGallery = (constructionGallery?.length ?? 0) > 0 && !props.offerGallery && !props.propertyGallery;
    useEffect(() => {
        if (viewType.current) {
            hitGalleryView({
                view_type: viewType.current,
                site,
                site_id,
                offer: offer.id
            });
        }
    }, [viewType.current]);

    useEffect(() => {
        setGalleryCounter(initialGalleryIndex);
    }, [initialGalleryIndex]);

    const trackingSharedData = {
        view_type: viewType.current ? viewType.current : "",
        site,
        site_id,
        offer: offer.id
    };

    const offer3dViewSrc = offer3dViewId != null ? getOffer3dViewLink(offer3dViewId, currentStage?.id, selectedDate, apiConstructionTours) : undefined;
    const hasConstructionTimeline = isConstructionGallery || (!props.offerGallery && !props.propertyGallery && offer3dViewMonths.length > 0);

    const onClose = () => {
        setInitialGalleryIndex(0);
        setIsModalOpen(false);

        hitClickOnGalleryClose(trackingSharedData);
    };

    const onGalleryImageClick = (index: number) => {
        if (showAccountPaywall) {
            return;
        }
        setIsModalOpen(true);
        setInitialGalleryIndex(index);
        hitClickOnGalleryImage({...trackingSharedData, image_position: index});
        const photoSection = isConstructionGalleryTab ? "construction_photos" : "gallery";
        trackAmplitudeOpenGallery({
            location: amplitudeGalleryContext?.location,
            offerId: offer.id,
            propertyId: propertyId,
            galleryView: "viewer",
            photoSection,
            photoIndex: index
        });
        if (amplitudeGalleryContext) {
            trackAmplitudeViewPhoto({
                location: amplitudeGalleryContext.location,
                offerId: offer.id,
                propertyId,
                photoIndex: index,
                photosTotal: galleryImages.length,
                photoSection,
                photoNavigation: "initial"
            });
        }
    };

    const offerGalleryLength = offerGallery?.length || 0;
    const propertyGalleryLength = propertyGallery ? propertyGallery.length : 0;

    const galleryLength = isConstructionGalleryTab ? galleryImages.length - 1 : offerGalleryLength + propertyGalleryLength;

    const handleViewerSlideChange = useCallback(
        (current: number, next: number) => {
            reset();
            setGalleryCounter(next);

            const trackingData = {
                ...trackingSharedData,
                image_position: next
            };

            if (current < next) {
                hitClickOnGalleryNext(trackingData);
            }

            if (current > next) {
                hitClickOnGalleryPrev(trackingData);
            }

            if (amplitudeGalleryContext && current !== next) {
                trackAmplitudeViewPhoto({
                    location: amplitudeGalleryContext.location,
                    offerId: offer.id,
                    propertyId,
                    photoIndex: next,
                    photosTotal: galleryImages.length,
                    photoSection: isConstructionGalleryTab ? "construction_photos" : "gallery",
                    photoNavigation: current < next ? "arrow_next" : "arrow_prev"
                });
            }
        },
        [reset, trackingSharedData, amplitudeGalleryContext, offer.id, propertyId, galleryImages.length, isConstructionGalleryTab]
    );

    const galleryGrid = (
        <GalleryPicturesGrid
            video={offer.video}
            shouldShowVideo={shouldShowVideo}
            getImageAlt={(index, isConstructionImage) => getGalleryImagesAltText(index, offer.name, isConstructionImage ?? isConstructionGallery)}
            images={galleryImages}
            onImageClick={onGalleryImageClick}
            lastTile={lastGalleryTile}
        />
    );

    return (
        <AdvancedScroll
            className={galleryHolder}
            disableScroll={showAccountPaywall}
            scrollTopShadowClassName={displayNone}
            scrollBottomShadowClassName={displayNone}
            contentClassName={scrollStyle}
            height={0}
        >
            {showAccountPaywall && (
                <ConstructionGalleryPaywallTrigger propertyId={propertyId} offerId={offer.id} photosAvailable construction3dTours={apiConstructionTours} />
            )}
            {hasConstructionTimeline && (
                <div className={cx(showAccountPaywall && blurContent)}>
                    <ConstructionTimelineSection
                        constructionGallery={constructionGallery ?? []}
                        constructionDateRange={offer.construction_date_range}
                        constructionEndDate={constructionEndDate}
                        policamLinksMonths={offer3dViewMonths}
                        selectedDate={selectedDate}
                        onDateSelect={setSelectedDate}
                    />
                </div>
            )}
            {isConstructionGalleryTab && hasOffer3dView(offer, apiConstructionTours) && offer3dViewSrc && (
                <section className={gallerySection}>
                    <Text variant="mini_header" className={gallerySectionHeader}>
                        WIDOK 3D
                    </Text>
                    <div className={cx(showAccountPaywall && blurContent)}>
                        {isMobile && (
                            <Text variant="body_copy_2" className={gallerySectionHeader}>
                                użyj palców aby przybliżać i obracać widok
                            </Text>
                        )}
                        <iframe
                            className={gallery3dIframe}
                            src={offer3dViewSrc}
                            title="Widok 3D"
                            sandbox="allow-scripts allow-same-origin allow-pointer-lock allow-fullscreen"
                            referrerPolicy="no-referrer"
                            allow="fullscreen"
                            loading="lazy"
                        />
                    </div>
                </section>
            )}
            <section className={gallerySection}>
                {isConstructionGallery && galleryImages.length > 0 && (
                    <Text variant="mini_header" className={gallerySectionHeader}>
                        ZDJĘCIA
                    </Text>
                )}
                <div className={cx(showAccountPaywall && blurContent)}>{galleryGrid}</div>
            </section>
            {/* Spread instead of zIndex={lightboxZIndex}: an explicit undefined would override the Modal default (9000) */}
            <Modal fullScreen isOpen={isModalOpen} modalTheme="dark" {...(lightboxZIndex === undefined ? {} : {zIndex: lightboxZIndex})} onModalClose={onClose}>
                <Modal.CloseButton placement="right" fillColor="#fff" />
                {isModalOpen && (
                    <Modal.Content>
                        <div ref={containerRef} className={gallerySliderHolder}>
                            <SliderGallery
                                disableSwipe={isZoomed}
                                slides={galleryImages.map((image, index) => (
                                    <div key={index} className={slideContainerStyle}>
                                        {image.tags && (
                                            <div className={tagContainer}>
                                                {image.tags.map((tag, tagIndex) => (
                                                    <Badge variant={tag.variant} icon={tag.icon} iconSize="1.5" key={tagIndex}>
                                                        {tag.label}
                                                    </Badge>
                                                ))}
                                            </div>
                                        )}

                                        {/* Same zoom style on every slide — outgoing slide must keep transform during slider transition. */}
                                        <picture style={getImageStyle()}>
                                            <source media="(min-width: 750px)" srcSet={image?.srcLg} />
                                            <source media="(min-width: 480px)" srcSet={image?.srcMd} />
                                            <img src={image?.srcSm} alt={offer.name} />
                                        </picture>
                                    </div>
                                ))}
                                arrows={{
                                    left: <SliderGalleryIcon position="left" wrapperColor="var(--colors-primary)" />,
                                    right: <SliderGalleryIcon position="right" wrapperColor="var(--colors-primary)" />
                                }}
                                initialSlide={initialGalleryIndex}
                                onSlideChange={handleViewerSlideChange}
                            />

                            <div className={counterHolder}>
                                <Text variant="info_txt_1" color="#fff">
                                    Zdjęcie: {galleryCounter + 1} z {galleryLength + 1}
                                </Text>
                            </div>
                        </div>
                    </Modal.Content>
                )}
            </Modal>
        </AdvancedScroll>
    );
};

/*
    Helper
 */
const getDefaultSelectedDate = (gallery: IConstructionGalleryResponse | undefined, offer3dViewMonths: string[]) => {
    const galleryMonths = (gallery ?? []).filter((item) => item?.tags?.tag_date).map((item) => item!.tags!.tag_date.slice(0, 7));
    const months = uniq([...galleryMonths, ...offer3dViewMonths]);
    return months.length > 0 ? months.sort((prev, next) => next.localeCompare(prev))[0] : null;
};

const getGalleryImagesData = (gallery?: IGallery) => {
    return gallery
        ? gallery
              .map((item) => {
                  const srcSm = item && item.image?.g_img_500;
                  const srcMd = item && item.image?.g_img_750;
                  const srcLg = item && item.image?.g_img_1500;

                  return item ? {srcSm, srcMd, srcLg} : null;
              })
              .filter(Boolean)
        : [];
};

const getConstructionGalleryImagesData = (gallery: IConstructionGalleryResponse) => {
    return gallery
        .map((item) => {
            const srcSm = item && getThumbnailLink(item.image, {width: 500});
            const srcMd = item && getThumbnailLink(item.image, {width: 750});
            const srcLg = item && getThumbnailLink(item.image, {width: 1500});

            const dateTag = item?.tags?.tag_date && {
                label: formatDate(item.tags.tag_date, "MMM yyyy"),
                variant: "label_info",
                icon: ClockIcon
            };
            const statusTag = item?.tags?.tag_status && {
                label: getConstructionGalleryTagLabel(item.tags.tag_status),
                variant: "label_warning",
                icon: FlagPoleIcon
            };

            return item ? {srcSm, srcMd, srcLg, tags: [dateTag, statusTag].filter(Boolean)} : null;
        })
        .filter(Boolean);
};

const getGalleryImages = (
    offer: IGalleryOffer,
    galleries: {showMainImage?: boolean; offerGallery?: IOfferGallery; propertyGallery?: IPropertyGallery; constructionGallery?: IConstructionGalleryResponse}
): (IGalleryImage | null)[] => {
    const propertyGalleryImages = (galleries.propertyGallery && getGalleryImagesData(galleries.propertyGallery)) || [];

    const mainImage = offer.main_image?.m_img_500
        ? {srcSm: offer.main_image?.m_img_500, srcMd: offer.main_image?.m_img_750, srcLg: offer.main_image?.m_img_1500}
        : {srcSm: presaleOfferDefaultImg375x212, srcMd: presaleOfferDefaultImg, srcLg: presaleOfferDefaultImg};

    const offerGalleryImages = getGalleryImagesData(galleries.offerGallery);

    const constructionGalleryImages = (galleries.constructionGallery && getConstructionGalleryImagesData(galleries.constructionGallery)) || [];

    return [...propertyGalleryImages, ...(galleries.showMainImage ? [mainImage] : []), ...offerGalleryImages, ...constructionGalleryImages];
};

const galleryHolder = css({
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",

    md: {
        flexDirection: "row"
    }
});

const gallerySection = css({
    display: "flex",
    flexDirection: "column",
    "&:not(:first-of-type)": {
        marginTop: 4
    }
});

const gallerySectionHeader = css({
    margin: 0,
    color: "white"
});

const gallery3dIframe = css({
    width: "100%",
    height: "56.25vw",
    border: "none",
    aspectRatio: "16/9",

    md: {
        width: "95%"
    }
});

const scrollStyle = css({
    "&::-webkit-scrollbar": {
        width: "4px"
    },
    "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#838595",
        "&:hover": {
            backgroundColor: "#83859599"
        }
    },
    "&::-webkit-scrollbar-track": {
        backgroundColor: "#686975"
    },

    md: {
        "&::-webkit-scrollbar": {
            width: "8px"
        }
    }
});

const displayNone = css({
    display: "none"
});

const blurContent = css({
    filter: "blur(10px)"
});

const gallerySliderHolder = css({
    position: "relative",
    w: "100%",
    h: "100%",
    // touch-action: none — custom pinch/pan + horizontal slider swipe (no native browser zoom).
    touchAction: "none",
    "& picture": {
        width: "100%",
        maxWidth: "100%"
    },
    "& picture img": {
        width: "100%",
        maxWidth: "100%",
        height: "100%",
        objectFit: "contain"
    }
});

const slideContainerStyle = css({
    width: "100%",
    height: "100%",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
});

const counterHolder = css({
    position: "absolute",
    bottom: "5rem",
    left: "50%",
    transform: "translate(-50%, 0%)",
    backgroundColor: "rgba(67, 68, 77, 0.7)",
    px: 2,
    py: 1
});

const tagContainer = css({
    position: "fixed",
    display: "flex",
    color: "var(--colors-secondary)",
    top: 1,
    gap: 1
});
